import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from "@angular/core";
import { IWizardNavigationState, IWizardStepChangedEvent, ModuleTabConfig, WizardNavigationService } from "@kiraah-shared/core";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "mobile-wizard-navigation",
  standalone: true,
  imports: [TranslateModule],
  templateUrl: "./mobile-wizard-navigation.component.html",
  styleUrl: "./mobile-wizard-navigation.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileWizardNavigationComponent {
  private readonly wizardNavigationService = inject(WizardNavigationService);
  readonly config = input<ModuleTabConfig | undefined>(undefined);
  readonly wizardInstanceId = input<string | null | undefined>(undefined);
  readonly isNavigating = input(false);
  readonly showSaveAsDraft = input(false);
  readonly isLastStep = input(false);
  readonly isSaving = input(false);
  readonly previousDisabled = input(false);
  readonly nextDisabled = input(false);
  readonly submitDisabled = input(false);
  readonly allowSubmit = input(true);
  readonly allowSaveAsDraft = input(true);

  private readonly navState = signal<IWizardNavigationState | null>(null);

  readonly previous = output<void>();
  readonly next = output<void>();
  readonly saveAsDraft = output<void>();
  readonly submit = output<void>();
  readonly wizardStepChanged = output<IWizardStepChangedEvent>();

  readonly effectiveShowSaveAsDraft = computed(() => {
    if (!this.allowSaveAsDraft()) {
      return false;
    }
    const navigationConfig = this.config();
    if (navigationConfig?.isWizardMode()) {
      return navigationConfig.showSaveAsDraftButton();
    }
    return this.showSaveAsDraft();
  });

  readonly labelPrevious = computed(() => this.config()?.getButtonText("previous") ?? "app.wizard.previous");
  readonly labelNext = computed(() => this.config()?.getButtonText("next") ?? "app.wizard.next");
  readonly labelComplete = computed(() => this.config()?.getButtonText("complete") ?? "app.wizard.complete");
  readonly labelSaveAsDraft = computed(() => this.config()?.getButtonText("saveAsDraft") ?? "app.wizard.saveasdraft");

  readonly effectivePreviousDisabled = computed(() => {
    if (this.wizardInstanceId()) {
      const navigationState = this.navState();
      return !navigationState?.canGoPrevious || this.isNavigating();
    }
    return this.previousDisabled();
  });

  readonly effectiveNextDisabled = computed(() => {
    if (this.wizardInstanceId()) {
      const navigationState = this.navState();
      return !navigationState?.canGoNext || this.isNavigating();
    }
    return this.nextDisabled();
  });

  readonly effectiveSubmitDisabled = computed(() => {
    if (this.wizardInstanceId()) {
      const navigationState = this.navState();
      return !navigationState?.canComplete || this.isNavigating();
    }
    return this.submitDisabled();
  });

  readonly effectiveDraftDisabled = computed(() => {
    if (this.wizardInstanceId()) {
      const navigationState = this.navState();
      return !navigationState?.canSaveAsDraft || this.isNavigating();
    }
    return this.isSaving();
  });

  readonly effectiveIsLastStep = computed(() => {
    if (this.wizardInstanceId()) {
      const navigationState = this.navState();
      return !!(navigationState && navigationState.totalSteps > 0 && navigationState.currentStepIndex >= navigationState.totalSteps - 1);
    }
    const navigationConfig = this.config();
    if (navigationConfig?.isWizardMode()) {
      return navigationConfig.isLastStep();
    }
    return this.isLastStep();
  });

  readonly navigationErrorMessageKey = computed(() =>
    this.wizardInstanceId() ? this.navState()?.errorMessageKey : undefined,
  );

  constructor() {
    effect((onCleanup) => {
      const wizardId = this.wizardInstanceId();
      if (!wizardId) {
        this.navState.set(null);
        return;
      }
      const navigationStream = this.wizardNavigationService.getNavigationState$(wizardId);
      if (!navigationStream) {
        this.navState.set(null);
        return;
      }
      const subscription = navigationStream.subscribe((navigationState: IWizardNavigationState) => this.navState.set(navigationState));
      onCleanup(() => subscription.unsubscribe());
    });
  }

  emitPrevious(): void {
    const wizardId = String(this.wizardInstanceId() ?? "");
    const step = this.navState()?.currentStepIndex ?? 0;
    this.wizardStepChanged.emit({
      step: step - 1,
      action: "previous",
      wizardInstanceId: wizardId,
      previousStep: step,
    });
    this.previous.emit();
  }

  emitNext(): void {
    const wizardId = String(this.wizardInstanceId() ?? "");
    const step = this.navState()?.currentStepIndex ?? 0;
    this.wizardStepChanged.emit({
      step: step + 1,
      action: "next",
      wizardInstanceId: wizardId,
      previousStep: step,
    });
    this.next.emit();
  }

  emitSaveAsDraft(): void {
    const wizardId = String(this.wizardInstanceId() ?? "");
    const step = this.navState()?.currentStepIndex ?? 0;
    this.wizardStepChanged.emit({
      step,
      action: "saveAsDraft",
      wizardInstanceId: wizardId,
      previousStep: step,
    });
    this.saveAsDraft.emit();
  }

  emitSubmit(): void {
    const wizardId = String(this.wizardInstanceId() ?? "");
    const step = this.navState()?.currentStepIndex ?? 0;
    this.wizardStepChanged.emit({
      step,
      action: "complete",
      wizardInstanceId: wizardId,
      previousStep: step,
    });
    this.submit.emit();
  }
}
